import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, ArrowRight, Clock, Mail } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_pre-order-hub/artifacts/my1fu1ea_scene%201.jpg";

const VEHICLE_NAMES = {
  adler: "Adler (Heimat Edition)",
  falke: "Falke",
  quelle: "Quelle Supercar",
  ritter: "Ritter",
  koenig: "König",
  stein: "Stein",
  enz: "Enz",
  panzer: "Panzer"
};

const VEHICLE_TIMELINES = {
  adler: "Test drive in approximately 3 months, with delivery targeting 9 months.",
  falke: "Production begins approximately 6 months after Adler deliveries.",
  quelle: "Estimated delivery in approximately 2 years.",
  ritter: "Design phase — timeline updates coming.",
  koenig: "Design phase — timeline updates coming.",
  stein: "Design phase — timeline updates coming.",
  enz: "Estimated delivery in approximately 3 years.",
  panzer: "Design phase — timeline updates coming."
};

// Animation variants - extracted to prevent re-renders
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerChildren = {
  visible: { transition: { staggerChildren: 0.15 } }
};

const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { delay: 1 }
};

// Success Icon Component
const SuccessIcon = () => (
  <motion.div variants={fadeInUp} className="mb-8">
    <div className="w-24 h-24 mx-auto rounded-full bg-green-500/10 flex items-center justify-center">
      <CheckCircle className="w-12 h-12 text-green-500" />
    </div>
  </motion.div>
);

// Vehicle Info Component
const VehicleInfo = ({ vehicleName }) => (
  <motion.div variants={fadeInUp} className="mb-6">
    <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">You've reserved</p>
    <p className="font-display text-2xl md:text-3xl font-bold text-primary">{vehicleName}</p>
  </motion.div>
);

// Next Steps Component
const NextSteps = ({ timeline, reservationId }) => (
  <motion.div variants={fadeInUp} className="space-y-4 mb-8">
    <p className="text-lg text-foreground/70">
      Thank you for your interest in Keller Motors. Your reservation has been recorded.
    </p>
    
    <div className="p-6 bg-card border border-border text-left space-y-4">
      <div className="flex items-start gap-3">
        <Mail className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-sm">What's Next</p>
          <p className="text-sm text-foreground/60">
            We're finalizing our legal and banking setup to ensure your deposit is fully protected. 
            You'll receive an email within the next few weeks with deposit instructions and a formal purchase contract.
          </p>
        </div>
      </div>
      
      <div className="flex items-start gap-3">
        <Clock className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-sm">Expected Timeline</p>
          <p className="text-sm text-foreground/60">
            {timeline} As this is our first production run, we ask for your patience and flexibility 
            — though our straightforward engineering approach means builds move fast.
          </p>
        </div>
      </div>
    </div>

    {reservationId && (
      <p className="text-xs text-muted-foreground">
        Reservation ID: {reservationId}
      </p>
    )}
  </motion.div>
);

// Actions Component
const SuccessActions = () => (
  <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
    <Link to="/">
      <button 
        className="bg-primary text-primary-foreground h-14 px-10 uppercase tracking-widest font-bold text-sm hover:bg-red-600 flex items-center justify-center gap-2"
        data-testid="back-to-home-btn"
      >
        Explore More Vehicles <ArrowRight className="w-4 h-4" />
      </button>
    </Link>
  </motion.div>
);

// Custom hook for fetching reservation
const useReservation = (reservationId) => {
  const [reservation, setReservation] = useState(null);

  const fetchReservation = useCallback(async () => {
    if (!reservationId) return;
    try {
      const response = await axios.get(`${API}/reservations/${reservationId}`);
      setReservation(response.data);
    } catch (error) {
      // Log error for debugging but don't disrupt UX
      console.warn("Could not fetch reservation details:", error.message);
    }
  }, [reservationId]);

  useEffect(() => {
    fetchReservation();
  }, [fetchReservation]);

  return reservation;
};

// Main Component
export default function SuccessPage() {
  const [searchParams] = useSearchParams();
  const reservationId = searchParams.get("id");
  const vehicleId = searchParams.get("vehicle") || "adler";
  
  const reservation = useReservation(reservationId);
  const vehicleName = VEHICLE_NAMES[vehicleId] || "Vehicle";
  const timeline = VEHICLE_TIMELINES[vehicleId] || "";

  return (
    <main className="min-h-screen bg-background relative" data-testid="success-page">
      {/* Background */}
      <div className="absolute inset-0">
        <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/90 to-[#050505]/80" />
      </div>

      <div className="noise-overlay" />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-24">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerChildren}
          className="text-center max-w-2xl"
        >
          {/* Logo */}
          <motion.div variants={fadeInUp}>
            <Link to="/" className="font-display font-bold text-lg tracking-tight mb-12 inline-block">
              KELLER MOTORS
            </Link>
          </motion.div>

          <SuccessIcon />

          {/* Title */}
          <motion.h1 
            variants={fadeInUp}
            className="font-display text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tighter mb-6"
            data-testid="success-title"
          >
            Reservation Confirmed
          </motion.h1>

          <VehicleInfo vehicleName={vehicleName} />
          <NextSteps timeline={timeline} reservationId={reservation?.id} />
          <SuccessActions />
        </motion.div>

        {/* Footer */}
        <motion.div 
          initial={fadeIn.initial}
          animate={fadeIn.animate}
          transition={fadeIn.transition}
          className="absolute bottom-8 text-center"
        >
          <p className="text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} Keller Motors. All rights reserved.
          </p>
        </motion.div>
      </div>
    </main>
  );
}
